import { SetStateAction } from 'react';
import { Button, IcoArrowLeftFilled } from '../index';
import './pagination.scss';

export interface IPagination {
  currentPage: number;
  totalPages: number;
  totalResults: number;
}

interface PaginationProps {
  paginationObj: IPagination;
  setPagination: React.Dispatch<SetStateAction<IPagination>>;
  wrapperStyle?: React.CSSProperties;
}

export const Pagination: React.FC<PaginationProps> = ({
  paginationObj,
  setPagination,
  wrapperStyle,
}) => {
  const isActive = (pageNumber: number | string) =>
    paginationObj.currentPage === pageNumber;
  const handleClickNumberedButton = (number: number | string) => {
    if (typeof number === 'string') return;
    setPagination({ ...paginationObj, currentPage: number });
  };
  const handleClickArrowUp = () => {
    setPagination({
      ...paginationObj,
      currentPage: paginationObj.currentPage + 1,
    });
  };
  const handleClickArrowDown = () => {
    setPagination({
      ...paginationObj,
      currentPage: paginationObj.currentPage - 1,
    });
  };

  const getPageNumbers = () => {
    const MAX_ELEMENTS_ON_DISPLAY = 7;
    let pageNumbers: number[] = [];
    for (let i = 1; i <= paginationObj.totalPages; i++) {
      pageNumbers.push(i);
    }
    if (pageNumbers.length <= MAX_ELEMENTS_ON_DISPLAY) return pageNumbers;
    const getCollapsedValues = () => {
      const MAX_ON_EACH_SIDE = 4;
      const FIRST_NUMBER = 1;
      const LAST_NUMBER = pageNumbers[pageNumbers.length - 1];
      let collapsedPages: Array<number | string> = [FIRST_NUMBER];
      const activePage = pageNumbers.find((number) => isActive(number)) || 1;
      const smallerThanActivePage = pageNumbers.filter(
        (number) => number < activePage
      );
      const biggerThanActivePage = pageNumbers.filter(
        (number) => number > activePage
      );
      let shouldCollapseStart =
        smallerThanActivePage.length >= MAX_ON_EACH_SIDE;
      let shouldCollapseEnd = biggerThanActivePage.length >= MAX_ON_EACH_SIDE;

      for (let i = 1; i < pageNumbers.length; i++) {
        if (shouldCollapseStart && shouldCollapseEnd) {
          collapsedPages.push(
            ...['...', activePage, activePage + 1, activePage + 2, '...']
          );
          break;
        }
        if (shouldCollapseStart) {
          if (pageNumbers.length - i < MAX_ON_EACH_SIDE) {
            i = activePage;
          } else {
            i = pageNumbers.length - MAX_ON_EACH_SIDE;
          }
          collapsedPages.push('...');
          shouldCollapseStart = false;
        }
        if (collapsedPages.length > MAX_ON_EACH_SIDE && shouldCollapseEnd) {
          collapsedPages.push('...');
          shouldCollapseEnd = false;
          break;
        }
        if (i !== 1) collapsedPages.push(i);
      }
      collapsedPages.push(LAST_NUMBER);
      return collapsedPages;
    };
    return getCollapsedValues();
  };
  const pageNumbers = getPageNumbers();
  return (
    <div className="pagination" style={wrapperStyle}>
      <Button
        cy="PaginationLeftArrow"
        disabled={isActive(1)}
        onClick={handleClickArrowDown}
      >
        <IcoArrowLeftFilled cy="PaginationLeft" />
      </Button>
      {pageNumbers.map((page: number | string, index: number) => (
        <Button
          cy={`Pagination[${page}]`}
          key={index}
          className={`btn normal ${isActive(page) ? 'active' : 'inactive'}`}
          style={{ color: `${isActive(page) ? '#FFF' : '#6F7171'}` }}
          onClick={() => handleClickNumberedButton(page)}
          disabled={page === '...'}
        >
          {page}
        </Button>
      ))}
      <Button
        cy="PaginationRightArrow"
        disabled={isActive(paginationObj.totalPages)}
        onClick={handleClickArrowUp}
      >
        <IcoArrowLeftFilled
          cy="PaginationLeft"
          style={{ transform: 'rotate(180deg)' }}
        />
      </Button>
    </div>
  );
};
